import { NgxPermissionsService } from 'ngx-permissions';
import { LocalStorageService } from '../services/local-storage.service';
import { Access_Functions } from '../constants/permissions.constant';

/**
 * Jingdong (JDL) sellers of the Customer Portal (OEM-7125).
 * The order tabs and Unsync WMS of a JD seller follow pc_type 10 of the logged-in user
 * (isJdSeller); everything about PD codes follows the login permissions. Contract: the
 * backend note "Order PD code: frontend note" (OEM-7340, 2026-10-16).
 */
export const JD_SELLER_PC_TYPE = 10;

export const isJdSeller = (storage: LocalStorageService): boolean =>
    storage.get('userData')?.pc_type == JD_SELLER_PC_TYPE;

/** JDL tenant flag, stored at login (createSession) from userData.pc_type 10. */
export const isJdlTenant = (storage: LocalStorageService): boolean =>
    storage.get('isJdlTenant') === true;

const isGranted = (
    permissions: NgxPermissionsService,
    names: string[]
): boolean => names.some((name) => !!permissions.getPermission(name));

/*
 * PD code permissions as the backend defines them (OEM-7340 permission note):
 *  - PD_CODE_READ (or _WRITE): see an order's PD code, load the PD code dropdown
 *    (GET orders/pd_codes) and bulk-assign (POST orders/bulk_assign_pd_code)
 *  - PD_CODE_WRITE: send b2c_order.pd_code_id; mandatory when creating a shipment order.
 *    Sending it without PD_CODE_WRITE answers 403, so only then is the field shown.
 */

/** The PD code of an order (grid column, order header): PD_CODE_READ or _WRITE. */
export const hasPdCodeAccess = (permissions: NgxPermissionsService): boolean =>
    isGranted(permissions, [
        Access_Functions.PD_CODE_READ,
        Access_Functions.PD_CODE_WRITE
    ]);

/**
 * The PD Code field of Create / edit shipment: PD_CODE_WRITE, the only permission the
 * create and update APIs accept pd_code_id with (mandatory on create, optional on edit).
 */
export const canSendPdCode = (permissions: NgxPermissionsService): boolean =>
    isGranted(permissions, [Access_Functions.PD_CODE_WRITE]);

/**
 * Assign / change a PD code (Action Required row action, bulk action and PD Code column
 * dropdown; the PD Code dropdown of an order in its remorse period): PD_CODE_WRITE,
 * PD_CODE_READ and Action Required write, which the bulk assign API needs.
 */
export const canAssignPdCode = (permissions: NgxPermissionsService): boolean =>
    [
        Access_Functions.PD_CODE_WRITE,
        Access_Functions.PD_CODE_READ,
        Access_Functions.ORDER_ACTION_REQUIRED_WRITE
    ].every((name) => isGranted(permissions, [name]));

/**
 * Remorse period (backend note, 2026-10-21): an order that just got its PD code waits for
 * the seller's remorse period (sub-status remorse_period_waiting, label "Remorse Period
 * Waiting") before it reaches Pick; meanwhile it is listed on RTS Pending. The backend
 * moves it on by itself and returns no end time. While it waits, only its PD code can
 * be changed (that restarts the wait).
 */
export const REMORSE_PERIOD_WAITING = 'remorse_period_waiting';

/** The order (row or shipment attributes) is waiting for the remorse period. */
export const isRemorsePeriodWaiting = (
    attributes: { substatus?: string } | null | undefined
): boolean =>
    (attributes?.substatus ?? '').trim().toLowerCase().replace(/\s+/g, '_') ===
    REMORSE_PERIOD_WAITING;

/**
 * Edit on an Action Required row of a JDL tenant: the permission of the tab,
 * Online = ONLINE_ORDER_EDIT, Offline = OFFLINE_ORDER_EDIT.
 */
export const canEditActionRequiredOrder = (
    permissions: NgxPermissionsService,
    problemTab: string
): boolean =>
    isGranted(permissions, [
        problemTab === 'offline'
            ? Access_Functions.OFFLINE_ORDER_EDIT
            : Access_Functions.ONLINE_ORDER_EDIT
    ]);

/**
 * Sub-tabs of Successful and E-commerce for a JD seller, in this order, on existing grid
 * routes: Pick = /created, RTS Pending = the existing Pending tab renamed, Ready To Ship
 * = /ready_to_ship.
 */
export const JD_ORDER_TABS: ReadonlyArray<{ name: string; segment: string }> = [
    { name: 'Pick', segment: 'created' },
    { name: 'RTS Pending', segment: 'pending' },
    { name: 'Ready To Ship', segment: 'ready_to_ship' },
    { name: 'Completed', segment: 'completed' },
    { name: 'Delivered', segment: 'delivered' },
    { name: 'Cancelled', segment: 'cancelled' }
];

/**
 * /created and /ready_to_ship have grid columns, queries and row actions only for DPD
 * sellers. For a JD seller the grid reads them as New and In-Process (status 2 and 4),
 * so they list and act exactly like those tabs; the URL keeps the JD tab.
 */
const JD_TAB_GRID_STATUS: Readonly<Record<string, string>> = {
    created: 'new',
    ready_to_ship: 'in-process'
};

export const jdGridStatus = (tabSegment: string): string =>
    JD_TAB_GRID_STATUS[tabSegment] ?? tabSegment;

/** A JD seller's tab in the URL: segment 4 on Successful, 5 on E-commerce. */
export const jdTabOfUrl = (segments: string[], orderType: string): string =>
    segments[orderType === 'marketplaces' ? 5 : 4];

/**
 * JD tabs with their own row and bulk actions: Pick (/created), RTS Pending (/pending),
 * Ready To Ship (/ready_to_ship), Completed and Delivered. Cancelled keeps the regular
 * actions.
 */
export type JdActionTab =
    | 'created'
    | 'pending'
    | 'ready_to_ship'
    | 'completed'
    | 'delivered';

export const JD_ACTION_TABS: ReadonlyArray<string> = [
    'created',
    'pending',
    'ready_to_ship',
    'completed',
    'delivered'
];

/** The JD tab of the URL when it has its own actions, else null. */
export const jdActionTab = (
    segments: string[],
    orderType: string
): JdActionTab | null => {
    const tab = jdTabOfUrl(segments, orderType);
    return JD_ACTION_TABS.includes(tab) ? (tab as JdActionTab) : null;
};

/**
 * Row actions of a JD tab: the permission that shows that tab group, _READ or _WRITE
 * (Successful: CONFIRMED / SUCCESSFUL_ORDER_*, E-commerce: ECOMMERCE / MARKETPLACE_ORDER_*).
 */
export const canUseJdTabActions = (
    permissions: NgxPermissionsService,
    orderType: string
): boolean =>
    isGranted(
        permissions,
        orderType === 'marketplaces'
            ? [
                  Access_Functions.ECOMMERCE_ORDER_READ,
                  Access_Functions.ECOMMERCE_ORDER_WRITE,
                  Access_Functions.MARKETPLACE_ORDER_READ,
                  Access_Functions.MARKETPLACE_ORDER_WRITE
              ]
            : [
                  Access_Functions.CONFIRMED_ORDER_READ,
                  Access_Functions.CONFIRMED_ORDER_WRITE,
                  Access_Functions.SUCCESSFUL_ORDER_READ,
                  Access_Functions.SUCCESSFUL_ORDER_WRITE
              ]
    );

/** The first JD sub-tab, opened instead of the removed All tab. */
export const JD_DEFAULT_ORDER_TAB = JD_ORDER_TABS[0].segment;

/** Where old Unsync WMS links of a JD seller go: E-commerce > Pick. */
export const JD_UNSYNC_WMS_REDIRECT = `/order/marketplaces/connected/all-orders/all/${JD_DEFAULT_ORDER_TAB}`;

/** Most orders one Assign PD Code bulk action takes. */
export const MAX_PD_CODE_BULK_ORDERS = 500;

/**
 * A row of GET orders/pd_codes: the seller's assigned Outbound codes, sorted by code.
 * pd_code_id is an integer and is sent as one (b2c_order.pd_code_id).
 */
export interface SellerPdCode {
    pd_code_id: number;
    pd_code: string;
    pd_code_name?: string;
}

/**
 * A Sales Platform Code of the seller (added in the admin portal, Customer Details >
 * Sales Platform Codes); one is chosen per store on Connect eCommerce.
 */
export interface SalesPlatformCode {
    sales_platform_code_id: number;
    /** Kept exactly as typed; it goes to JDL unchanged. */
    sales_platform_code: string;
}

/**
 * JDL tenant order grids (Successful Orders tabs, Action Required, Blocked): no
 * Warehouse, Consignee or Type column, and no Warehouse or Order Type filter.
 */
export const JDL_HIDDEN_ORDER_COLUMNS: ReadonlyArray<string> = [
    'Warehouse',
    'Consignee',
    'Type'
];
export const JDL_HIDDEN_ORDER_CHIPS: ReadonlyArray<string> = [
    'order_warehouse',
    'order_type'
];

/** The grid columns without the ones a JDL tenant does not see (by column name). */
export const withoutJdlHiddenColumns = <T extends { name?: string }>(cols: T[]): T[] =>
    (cols ?? []).filter((col) => !JDL_HIDDEN_ORDER_COLUMNS.includes(col?.name ?? ''));

/** The filter chips without the ones a JDL tenant does not see. */
export const withoutJdlHiddenChips = <T extends { id: string }>(chips: T[]): T[] =>
    (chips ?? []).filter((chip) => !JDL_HIDDEN_ORDER_CHIPS.includes(chip.id));

/**
 * Configurations > Pick List (JDL): menu entry, the pick-list route and the Pick List
 * labels and fields of the packing list screen. ORDER_PICK_LIST only, no other flag.
 */
export const canUsePickList = (permissions: NgxPermissionsService): boolean =>
    isGranted(permissions, [Access_Functions.ORDER_PICK_LIST]);

/** The Sales Platform Code field of Connect eCommerce: SALES_PLATFORM_CODE_WRITE. */
export const canChooseSalesPlatformCode = (
    permissions: NgxPermissionsService
): boolean => isGranted(permissions, [Access_Functions.SALES_PLATFORM_CODE_WRITE]);

/** "CODE (Name)" for the PD code dropdowns. */
export const pdCodeLabel = (code: SellerPdCode, withName: boolean = true): string =>
    withName && code.pd_code_name ? `${code.pd_code} (${code.pd_code_name})` : code.pd_code;

/**
 * How the PD code cell edits: 'assign' = a dropdown where the order has no PD code
 * (Action Required); 'remorse' = a dropdown, set to the saved code, only on orders in
 * their remorse period (order tabs); 'text' = never (the read-only column of the
 * lib-anc-grid screens). Otherwise the cell shows the PD code text.
 */
export type PdCodeCellMode = 'assign' | 'remorse' | 'text';

/**
 * Grid column with the PD code text of the order, placed right after Order Number.
 * editable: a dropdown to assign a PD code where the order has none (Action Required).
 * changeWhileRemorse: a dropdown to change the PD code of an order in its remorse period.
 * Both are for users who can assign (canAssignPdCode); the PD code cell renders them.
 */
export const withPdCodeColumn = <T extends { map?: string }>(
    cols: T[],
    options: { editable?: boolean; changeWhileRemorse?: boolean } = {}
): T[] => {
    if (cols.some((col) => col?.map === 'pd_code')) return cols;
    const pdCodeMode: PdCodeCellMode | null = options.editable
        ? 'assign'
        : options.changeWhileRemorse
          ? 'remorse'
          : null;
    const pdCodeColumn = {
        name: 'PD Code',
        isChecked: true,
        map: 'pd_code',
        method: pdCodeMode ? 'parsePdCodeSelect' : 'parsePdCode',
        pdCodeMode
    } as unknown as T;
    const orderNumberIndex = cols.findIndex(
        (col) => col?.map === 'order_number'
    );
    const insertAt = orderNumberIndex === -1 ? 0 : orderNumberIndex + 1;
    return [...cols.slice(0, insertAt), pdCodeColumn, ...cols.slice(insertAt)];
};

/**
 * JDL packages (backend "JDL Package API", spec 018): the DPD package pop-up plus each
 * box's length, width, height and gross weight. Pick: the package count dropdown where
 * the order's carrier_type is 3, else the count; the JD tabs after Pack show the count.
 */
export const JD_PACKED_TABS: ReadonlyArray<string> = [
    'pending',
    'ready_to_ship',
    'completed',
    'delivered'
];

/** Pick tab row: the package dropdown (pop-up for 2+ boxes) only for carrier_type 3. */
export const isJdPackageEditable = (
    attributes: { carrier_type?: number | string } | null | undefined
): boolean => attributes?.carrier_type == 3;

/** One box of a JDL package (package_details[i] of item_package_assignment). */
export interface JdPackageDetails {
    length: number | string;
    width: number | string;
    height: number | string;
    gross_weight: number | string;
}

/** Every box needs length, width, height and gross weight, all numbers above 0. */
export const isJdPackageDetailsValid = (details: JdPackageDetails): boolean =>
    [details?.length, details?.width, details?.height, details?.gross_weight].every(
        (value) => value !== '' && value != null && Number(value) > 0
    );

/**
 * JD seller grid: "No of Packages" after the PD Code column (or Order Number). Pick
 * (created): the package cell; the tabs after Pack: no_of_packages as text.
 */
export const withJdPackageColumn = <T extends { map?: string; parseComponent?: string }>(
    cols: T[],
    tab: string
): T[] => {
    const isPick = tab === 'created';
    if (!isPick && !JD_PACKED_TABS.includes(tab)) return cols;
    if (cols.some((col) => col?.map === 'no_of_packages' || col?.parseComponent === 'parsepackage')) {
        return cols;
    }
    const packageColumn = (
        isPick
            ? { name: 'No of Packages', isChecked: true, map: 'no_of_packages', parseComponent: 'parsejdpackage' }
            : { name: 'No of Packages', isChecked: true, map: 'no_of_packages', method: 'parseCheckNull' }
    ) as unknown as T;
    const anchor = cols.findIndex((col) => col?.map === 'pd_code');
    const orderNumberIndex = cols.findIndex((col) => col?.map === 'order_number');
    const after = anchor !== -1 ? anchor : orderNumberIndex;
    const insertAt = after === -1 ? cols.length : after + 1;
    return [...cols.slice(0, insertAt), packageColumn, ...cols.slice(insertAt)];
};

/**
 * data of POST orders/bulk_assign_pd_code. It overwrites a saved code and changes only the
 * orders that are still editable. A call that assigned nothing still answers 200 (with
 * level "error"), so read success_count and failed_orders, not the status.
 */
export interface PdCodeBulkAssignResult {
    success_count: number;
    failure_count: number;
    failed_orders: Array<{
        order_id: number;
        reason: 'not_found' | 'not_editable' | string;
    }>;
}

/**
 * JDL pick lists and invoices (backend note OEM-7406): POST orders/pick_documents answers
 * one PDF for 1 to 200 orders. pick_list needs ORDER_PICK_LIST, invoice ORDER_INVOICE,
 * both needs both.
 */
export type JdDocuments = 'pick_list' | 'invoice' | 'both';

export const JD_DOCUMENTS_MAX_ORDERS = 200;

/** Pack Order (orders/bulk_rts) takes 1 to 200 orders. */
export const JD_PACK_MAX_ORDERS = 200;

/**
 * bulk_rts answers result 100 with "N sent, N queued, N skipped, N failed." and level
 * "error" when no order went through, so the toast follows the level.
 */
export const jdPackMessageCode = (res: { result?: number; level?: string }): number =>
    res?.result == 100 && res?.level !== 'error' ? 100 : 101;

export const canDownloadJdDocuments = (
    permissions: NgxPermissionsService,
    documents: JdDocuments
): boolean => {
    const pickList = isGranted(permissions, [Access_Functions.ORDER_PICK_LIST]);
    const invoice = isGranted(permissions, [Access_Functions.ORDER_INVOICE]);
    if (documents === 'pick_list') return pickList;
    if (documents === 'invoice') return invoice;
    return pickList && invoice;
};

/** An order left out of the PDF (X-Skipped-Orders header, at most the first 50). */
export interface JdSkippedOrder {
    order_id: number;
    reason:
        | 'not_found'
        | 'pick_list_missing'
        | 'invoice_missing'
        | 'pick_list_unreadable'
        | 'invoice_unreadable'
        | string;
}

/** The user-facing text of a skip reason. */
export const JD_SKIP_REASON_LABELS: Readonly<Record<string, string>> = {
    not_found: 'not found',
    pick_list_missing: 'pick list missing',
    invoice_missing: 'invoice missing',
    pick_list_unreadable: 'pick list could not be read',
    invoice_unreadable: 'invoice could not be read'
};

/**
 * Service mapping by carrier only (backend note OEM-7398): the backend has no flag in a
 * response; it judges the SERVICE_ONLY_MAPPING permission of the logged-in user.
 */
export const isServiceOnlyMapping = (permissions: NgxPermissionsService): boolean =>
    isGranted(permissions, [Access_Functions.SERVICE_ONLY_MAPPING]);

/** Action Required reason 9, "Service mapping missing" (not reason 10, no shipping method). */
export const isServiceMappingMissing = (
    attributes: Record<string, any> | null | undefined
): boolean =>
    !!attributes?.service_mapping_missing ||
    [attributes?.problem_reasons, attributes?.problem_state].some((reasons) =>
        `${Array.isArray(reasons) ? reasons.join(',') : reasons ?? ''}`.includes(
            'Service mapping missing'
        )
    );

/**
 * Action Required, Map Service for a service-only seller: the Service Mapping screen saves
 * with update_store_configuration (CUSTOMER_CONFIG_CARRIER_STORE_WRITE).
 */
export const canMapServiceOnly = (permissions: NgxPermissionsService): boolean =>
    isServiceOnlyMapping(permissions) &&
    isGranted(permissions, [Access_Functions.CUSTOMER_CONFIG_CARRIER_STORE_WRITE]);

